"use client";

import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";

import HorizontalRule from "apps/website/components/base/HorizontalRule/HorizontalRule";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import {
  MessagingCard,
} from "apps/website/components/feature/MessagingCard/MessagingCard";
import { Theme } from "apps/website/maps/Theme.map";

import { PayPalExpressCheckoutV1 } from "./v1/PayPalExpressCheckoutV1";
import { PayPalExpressCheckoutV2 } from "./v2/PayPalExpressCheckoutV2";

export type PayPalExpressCheckoutVersion = "v1" | "v2";

/** Picks the version-specific wrapper at render time so each one can call its
 * own `useCreateBasket` hook unconditionally (Rules of Hooks): only one of
 * the V1/V2 trees is mounted, but inside whichever one is mounted the hook
 * call order stays stable. A missing / zero `price` short-circuits to `null`
 * here so neither version's `useCreateBasket` runs when there's nothing to
 * charge. */
export function PayPalExpressCheckout({
  version,
  price,
  onSuccess,
  dividerBackgroundColour,
}: Readonly<{
  version: PayPalExpressCheckoutVersion;
  price: number | undefined;
  onSuccess: () => void;
  /** Should match the ancestor section's background so the divider line
   * appears interrupted by the label rather than passing through a white
   * rectangle. */
  dividerBackgroundColour?: Theme;
}>) {
  const t = useTranslations("Shared.PayPalExpressCheckout");
  const [ error, setError ] = useState<string | undefined>(undefined);
  const handleError = useCallback((message: string) => setError(message), []);

  if (!price) {
    return null;
  }

  const button = version === "v1" ? (
    <PayPalExpressCheckoutV1
      price={ price }
      onSuccess={ onSuccess }
      onError={ handleError }
    />
  ) : (
    <PayPalExpressCheckoutV2
      price={ price }
      onSuccess={ onSuccess }
      onError={ handleError }
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <TextSubtitle size={2} align="center">
        { t("completePaymentWith") }
      </TextSubtitle>
      { button }
      { error && (
        <MessagingCard
          component="functional"
          title={ t("Errors.title") }
          description={ error }
          icon={{ icon: "warningBold", size: "xsmall", scale: true }}
          styleOptions={{ backgroundColor: "light-grey-75" }}
        />
      ) }
      <HorizontalRule
        styleOptions={ dividerBackgroundColour ? { backgroundColour: dividerBackgroundColour } : undefined }
      >
        { t("orEnterCardDetails") }
      </HorizontalRule>
    </div>
  );
}
